import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DSSetup, Service } from 'webapp-types';

import FormCompareDropdownComponent from 'webapp/components/form/compare/dropdown';
import MetricsPopupCompareOtherMetricComponent from 'webapp/components/metrics/popup-compare-other-metric';

import type { Goal } from 'webapp/models-custom';
import type DataSelect from 'webapp/models-custom/data-select';
import DsCompare from 'webapp/models-custom/ds-compare';

interface Args {
	model: DsCompare;
	dataSelect: DataSelect; // for goal compare edit
	onChange: (compare: DsCompare, skipMetricSelectPopup?: boolean) => void;
	exceptionIds?: string[];
	placeholder?: string;
	isDisabled?: boolean;
	theme?: string;
	size?: string;
	origin?: string;
}

export default class FormSelectCompareTypeComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('navigation') declare navigationService: Service.Navigation;

	get metricLimitations() {
		return this.args.dataSelect.limitations;
	}

	// The native app compares only within what's there: it can't create a goal or pick another metric.
	get isMobileApp(): boolean {
		return this.navigationService.isMobileApp;
	}

	get exceptionIds(): string[] {
		const exceptionIds = this.args.exceptionIds ?? [];
		if (!this.isMobileApp) {
			return exceptionIds;
		}

		const hasGoals = !!this.mmGoalsService.getValidGoalsForMetricSetup(
			this.args.dataSelect.metricSetup,
		).length;
		return hasGoals ? exceptionIds : [...exceptionIds, String(DsCompare.Category.GOAL)];
	}

	get showOtherMetricCompare(): boolean {
		return !this.isMobileApp;
	}

	get showEdit() {
		if (this.isMobileApp) {
			return this.args.model.isGoal && !this.isGoalButEmpty;
		}
		return this.args.model.isGoal || this.args.model.isOtherMetric;
	}

	get validGoalsForSelection() {
		if (this.args.model.isGoal) {
			const dataSelect = this.args.dataSelect;
			return this.mmGoalsService.getValidGoalsForMetricSetup(dataSelect.metricSetup);
		} else {
			return [];
		}
	}

	get isGoalButEmpty() {
		if (this.args.model.isGoal) {
			return !this.validGoalsForSelection.length;
		} else {
			return false;
		}
	}

	get editText() {
		let text = 'change';
		if (this.isGoalButEmpty) {
			text = 'set goal';
		}
		return text;
	}

	get origin() {
		return this.args.origin ?? this.args.dataSelect.block?.trackingOrigin;
	}

	get extraClass() {
		const cssClasses: string[] = [];

		if (this.showEdit) {
			cssClasses.push('show-padding-edit');
		}

		return cssClasses.join(' ');
	}

	@action
	handleChange(compare: DsCompare, skipMetricSelectPopup?: boolean) {
		if (skipMetricSelectPopup) {
			this.args.onChange(compare, true);
			return;
		}

		if (compare.isOtherMetric) {
			void this.mmMetricsService.openMetricSelectPopup({
				onChange: (metricData: DSSetup.MetricData) => {
					compare.metricSetup.setFromMetricData(metricData);
					this.args.onChange(compare, true);
				},
			});
			return;
		}

		if (compare.isGoal) {
			const validGoals = this.mmGoalsService.getValidGoalsForMetricSetup(
				this.args.dataSelect.metricSetup,
			);
			if (validGoals.length === 0) {
				this.openNewGoalPopup(compare);
			} else {
				this.openGoalSelectorPopup(compare);
			}
			return;
		}

		this.args.onChange(compare, false);
	}

	@action
	editAction() {
		if (this.args.model.isGoal) {
			if (this.isGoalButEmpty) {
				this.openNewGoalPopup(this.args.model);
			} else {
				this.openGoalSelectorPopup(this.args.model);
			}
		} else if (this.args.model.isOtherMetric) {
			this.openOtherMetricPopup(this.args.model, true);
		}
	}

	openOtherMetricPopup(compare: DsCompare, skipMetricSelectPopup = false) {
		const popup = this.popupService.createModal(MetricsPopupCompareOtherMetricComponent, {
			allowStacked: true,
		});
		const onSave = (newCompare: DsCompare) => {
			this.args.onChange(newCompare, skipMetricSelectPopup);
		};
		popup.open({
			model: compare,
			onSave,
		});
	}

	openGoalSelectorPopup(compare: DsCompare) {
		this.mmGoalsService.openGoalSelectorPopup({
			selectedGoalMetricData: compare.metricSetup.metricData,
			metricSetup: this.args.dataSelect.metricSetup,
			onSave: (metricData: DSSetup.MetricData | null) => {
				this.applyMetricSetupToCompare(compare, metricData);
			},
		});
	}

	openNewGoalPopup(compare: DsCompare) {
		if (this.isMobileApp) {
			return;
		}

		this.mmGoalsService.openNewPopupForMetricSetup(
			this.args.dataSelect.metricSetup,
			'',
			(model: Goal) => {
				const goalKeyLong = model.goalKeyLong;
				const validGoals = this.mmGoalsService.getValidGoalsForMetricSetup(
					this.args.dataSelect.metricSetup,
				);
				if (validGoals.find((item) => item.goalKeyLong === goalKeyLong)) {
					this.applyMetricSetupToCompare(compare, {
						key: goalKeyLong,
						spaceAccessId: model.mainMetricSetupSpaceAccessId,
					});
				} else {
					this.infolayerService.warning(
						'The goal you created is not compatible with the selection',
					);
				}
			},
			true, // disable manual goals which can't be selected as a compare
			true, // disable objective type goal which can't be selected as a compare
		);
	}

	applyMetricSetupToCompare(compare: DsCompare, metricData: DSSetup.MetricData | null) {
		const newCompare = compare.createCopy();
		newCompare.metricSetup.setFromMetricData(metricData);
		this.args.onChange(newCompare, true);
	}
	<template>
		<div class="compare--type">

			<FormCompareDropdownComponent
				@metricLimitations={{this.metricLimitations}}
				@compare={{@model}}
				@exceptionIds={{this.exceptionIds}}
				@showOtherMetricCompare={{this.showOtherMetricCompare}}
				@onChange={{this.handleChange}}
				@isDisabled={{@isDisabled}}
				@placeholder={{@placeholder}}
				@theme={{@theme}}
				@size={{@size}}
				@extraClass={{this.extraClass}}
				@trackingOrigin={{this.origin}}
			/>

			{{#if this.showEdit}}
				<a class="mk-edit-button mk-edit-button--{{@theme}}" {{on "click" this.editAction}}>
					{{this.editText}}
				</a>
			{{/if}}

		</div>
	</template>
}
